import dayjs from 'dayjs';
import { ReportDataFormats } from '@reports/domain/types';
import type { ReportDataConfig } from '@reports/domain/types';

/**
 * Formats a raw string value based on the provided data configuration
 *
 * Handles different data formats (TEXT, CURRENCY, NUMBER, DATE, etc.) and applies
 * appropriate formatting rules based on the configuration options.
 *
 * @param {string} value - Raw string value to format
 * @param {ReportDataConfig} config - Configuration object containing format type and options
 * @returns {string} Formatted value ready for display
 *
 * @example
 * formatCellValue('2850.00', { type: ReportDataFormats.CURRENCY, options: { decimals: 2, prefix: '$' } })
 * // Returns: '$2,850.00'
 *
 * @example
 * formatCellValue('Total sales', { type: ReportDataFormats.TEXT })
 * // Returns: 'Total sales'
 */
export const formatCellValue = (value: string, config: ReportDataConfig): string => {
  try {
    switch (config.type) {
      case ReportDataFormats.TEXT:
        return value ? formatText(value) : '-';

      case ReportDataFormats.CURRENCY:
        return formatCurrency(value, config.options);

      case ReportDataFormats.NUMBER:
        return formatNumber(value, config.options);

      case ReportDataFormats.DATE:
        return formatDate(value, config.options);

      case ReportDataFormats.PERCENTAGE:
        return formatPercentage(value, config.options);

      case ReportDataFormats.BOOLEAN:
        return formatBoolean(value);

      default:
        return value;
    }
  } catch (error) {
    return value;
  }
};

/**
 * Formats text values (no transformation)
 *
 * @param {string} value - Text value to format
 * @returns {string} Original text value
 */
const formatText = (value: string): string => {
  return value;
};

/**
 * Formats currency values with locale-specific formatting
 *
 * Applies thousand separators, decimal places, and currency prefix/suffix.
 *
 * @param {string} value - Numeric string value
 * @param {Object} [options] - Formatting options
 * @param {number} [options.decimals=2] - Number of decimal places
 * @param {string} [options.prefix='$'] - Currency prefix (e.g., '$', '€')
 * @param {string} [options.suffix] - Currency suffix (optional)
 * @returns {string} Formatted currency string
 *
 * @example
 * formatCurrency('2850', { decimals: 2, prefix: '$' })
 * // Returns: '$2,850.00'
 */
const formatCurrency = (
  value: string,
  options?: {
    decimals?: number;
    prefix?: string;
    suffix?: string;
  },
): string => {
  const decimals = options?.decimals ?? 2;
  const prefix = options?.prefix ?? '$';
  const suffix = options?.suffix ?? '';

  const numericValue = parseFloat(value);

  if (isNaN(numericValue)) {
    return value;
  }

  const isNegative = numericValue < 0;
  const absoluteValue = Math.abs(numericValue);

  const formattedNumber = absoluteValue.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  return isNegative ? `-${prefix}${formattedNumber}${suffix}` : `${prefix}${formattedNumber}${suffix}`;
};

/**
 * Formats numeric values with locale-specific formatting
 *
 * Applies thousand separators and decimal places.
 *
 * @param {string} value - Numeric string value
 * @param {Object} [options] - Formatting options
 * @param {number} [options.decimals] - Number of decimal places (optional)
 * @returns {string} Formatted number string
 *
 * @example
 * formatNumber('1234567.89', { decimals: 2 })
 * // Returns: '1,234,567.89'
 */
const formatNumber = (
  value: string,
  options?: {
    decimals?: number;
  },
): string => {
  const numericValue = parseFloat(value);

  if (isNaN(numericValue)) {
    return value;
  }

  const formatOptions: Intl.NumberFormatOptions =
    options?.decimals !== undefined
      ? {
          minimumFractionDigits: options.decimals,
          maximumFractionDigits: options.decimals,
        }
      : {};

  return numericValue.toLocaleString('en-US', formatOptions);
};

/**
 * Formats date values using dayjs
 *
 * Parses dates in DD/MM/YYYY format (as received from the API) and formats them
 * according to the specified output format.
 *
 * @param {string} value - Date string in DD/MM/YYYY format (e.g., "13/02/2025")
 * @param {Object} [options] - Formatting options
 * @param {string} [options.dateFormat='DD/MM/YYYY'] - Output date format pattern (dayjs format)
 * @returns {string} Formatted date string
 *
 * @example
 * formatDate('13/02/2025', { dateFormat: 'DD MMM YYYY' })
 * // Returns: '13 Feb 2025'
 *
 * @example
 * formatDate('12/02/2025')
 * // Returns: '12/02/2025'
 */
const formatDate = (
  value: string,
  options?: {
    dateFormat?: string;
  },
): string => {
  const dateFormat = options?.dateFormat ?? 'DD/MM/YYYY';

  const parsedDate = dayjs(value, 'DD/MM/YYYY');

  if (!parsedDate.isValid()) {
    return value;
  }

  return parsedDate.format(dateFormat);
};

/**
 * Formats percentage values
 *
 * @param {string} value - Numeric string value (e.g., '0.15' for 15%)
 * @param {Object} [options] - Formatting options
 * @param {number} [options.decimals=2] - Number of decimal places
 * @returns {string} Formatted percentage string
 *
 * @example
 * formatPercentage('0.1575', { decimals: 2 })
 * // Returns: '15.75%'
 */
const formatPercentage = (
  value: string,
  options?: {
    decimals?: number;
  },
): string => {
  const decimals = options?.decimals ?? 2;
  const numericValue = parseFloat(value);

  if (isNaN(numericValue)) {
    return value;
  }

  const percentageValue = numericValue * 100;

  return `${percentageValue.toFixed(decimals)}%`;
};

/**
 * Formats boolean values
 *
 * @param {string} value - Boolean string value ('true', 'false', '1', '0')
 * @returns {string} Formatted boolean string ('Si' or 'No')
 *
 * @example
 * formatBoolean('true')
 * // Returns: 'Si'
 *
 * @example
 * formatBoolean('0')
 * // Returns: 'No'
 */
const formatBoolean = (value: string): string => {
  const lowercaseValue = value.toLowerCase();

  if (lowercaseValue === 'true' || lowercaseValue === '1') {
    return 'Si';
  }

  if (lowercaseValue === 'false' || lowercaseValue === '0') {
    return 'No';
  }

  return value;
};

/**
 * Formats axis values with abbreviated notation (K, M, B)
 *
 * Formats large numbers using abbreviated notation for better readability on charts.
 * Values under 1,000 are shown as-is, while larger values use K (thousands),
 * M (millions), or B (billions) suffixes.
 *
 * @param {number} value - Numeric value to format
 * @returns {string} Formatted abbreviated number string
 *
 * @example
 * formatAxisValue(100)
 * // Returns: '100'
 *
 * @example
 * formatAxisValue(1000)
 * // Returns: '1K'
 *
 * @example
 * formatAxisValue(1500000)
 * // Returns: '1.5M'
 *
 * @example
 * formatAxisValue(2500000000)
 * // Returns: '2.5B'
 */
export const formatAxisValue = (value: number): string => {
  if (isNaN(value) || value === null || value === undefined) {
    return '';
  }

  // Hide zero values on axis
  if (value === 0) {
    return '';
  }

  const absoluteValue = Math.abs(value);
  const isNegative = value < 0;
  const sign = isNegative ? '-' : '';

  // Values under 1,000 - show as-is
  if (absoluteValue < 1000) {
    return `${sign}${absoluteValue}`;
  }

  // Thousands (1K - 999K)
  if (absoluteValue < 1000000) {
    const thousands = absoluteValue / 1000;
    const rounded = Math.round(thousands * 10) / 10;
    const formatted = rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(1);
    return `${sign}${formatted}K`;
  }

  // Millions (1M - 999M)
  if (absoluteValue < 1000000000) {
    const millions = absoluteValue / 1000000;
    const rounded = Math.round(millions * 10) / 10;
    const formatted = rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(1);
    return `${sign}${formatted}M`;
  }

  // Billions (1B+)
  const billions = absoluteValue / 1000000000;
  const rounded = Math.round(billions * 10) / 10;
  const formatted = rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(1);
  return `${sign}${formatted}B`;
};
